import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CgList } from "react-icons/cg";
import { IoAddOutline } from "react-icons/io5";
import { HiOutlineSearch } from "react-icons/hi";
import { MdClear, MdDriveFileRenameOutline } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";

import Button from "../Utilities/Button";
import PaginationNew from "../Utilities/PaginationNew";
import RightPanelLayout from "../Layout/RightPanelLayout";
import Alert from "../Utilities/Alert";
import Toast from "../Utilities/Toast";
import Switcher from "../Utilities/AlertSwitch/Switcher";
import Modal from "../Utilities/Modal";

import styles from "./style/AiLearningResourcesList.module.scss";
import {
  deleteAiLearningResource,
  fetchAiLearningResourceDownloads,
  fetchAiLearningResourcesList,
  patchAiLearningResourceDisplay,
} from "./aiLearningResourcesApi";
import {
  EMPTY_DISPLAY,
  formatUploadedDateOnly,
  normalizeAiLearningResourceListRow,
} from "./aiLearningResourcesTransforms";

const LIMIT = 10;
const DOWNLOAD_VIEW_MODES = [
  { key: "each", label: "每次" },
  { key: "day", label: "日" },
  { key: "month", label: "月" },
  { key: "year", label: "年" },
];

const parseToDate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const normalized = raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
};

const pad2 = (n) => String(n).padStart(2, "0");
const formatDateKey = (date, mode) => {
  const yyyy = date.getFullYear();
  const mm = pad2(date.getMonth() + 1);
  const dd = pad2(date.getDate());
  if (mode === "year") return String(yyyy);
  if (mode === "month") return `${yyyy}.${mm}`;
  return `${yyyy}.${mm}.${dd}`;
};

const formatEachDate = (date) => {
  const yyyy = date.getFullYear();
  const mm = pad2(date.getMonth() + 1);
  const dd = pad2(date.getDate());
  const hh = pad2(date.getHours());
  const mi = pad2(date.getMinutes());
  const ss = pad2(date.getSeconds());
  return `${yyyy}.${mm}.${dd} ${hh}:${mi}:${ss}`;
};

const mutedUnset = (value) =>
  value === EMPTY_DISPLAY ? (
    <span className={styles.placeholderText}>{EMPTY_DISPLAY}</span>
  ) : (
    value
  );

const AiLearningResourcesList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [downloadsModalOpen, setDownloadsModalOpen] = useState(false);
  const [downloadsLoading, setDownloadsLoading] = useState(false);
  const [downloadsRows, setDownloadsRows] = useState([]);
  const [downloadsViewMode, setDownloadsViewMode] = useState("each");
  const [downloadsTargetTitle, setDownloadsTargetTitle] = useState("");
  const [toastState, setToastState] = useState({
    show: false,
    title: "",
    subtitle: "",
    error: false,
  });

  const showToast = (title, error = false) => {
    setToastState({ show: true, title, subtitle: "", error });
    window.setTimeout(() => {
      setToastState((s) => ({ ...s, show: false }));
    }, 3200);
  };

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await fetchAiLearningResourcesList({
        search: searchApplied,
        page,
        limit: LIMIT,
      });
      const apiRows = data?.rows ?? data?.items ?? [];
      setRows(apiRows.map(normalizeAiLearningResourceListRow));
      setTotal(Number(data?.total) || 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setListLoading(false);
    }
  }, [page, searchApplied]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / LIMIT)),
    [total],
  );

  const onPageChange = (selectedObj) => {
    setPage(selectedObj.selected + 1);
  };

  const applyListSearch = () => {
    setSearchApplied(searchInput.trim());
    setPage(1);
  };

  const handleDelete = async () => {
    if (!deleteTargetId || deleteSubmitting) return;
    setDeleteSubmitting(true);
    try {
      await deleteAiLearningResource(deleteTargetId);
      setDeleteTargetId(null);
      showToast("已刪除教學資源卡片");
      await loadList();
    } catch {
      showToast("刪除失敗請重試", true);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleTogglePublished = async (id, currentValue) => {
    const nextValue = !Boolean(currentValue);
    setRows((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, isPublished: nextValue } : item,
      ),
    );
    try {
      await patchAiLearningResourceDisplay(id, nextValue);
    } catch {
      setRows((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isPublished: Boolean(currentValue) } : item,
        ),
      );
      showToast("上架狀態更新失敗", true);
    }
  };

  const aggregatedDownloadRows = useMemo(() => {
    if (downloadsRows.length === 0) return [];

    if (downloadsViewMode === "each") {
      return downloadsRows
        .map((row) => {
          const d = parseToDate(row?.downloaded_at);
          if (!d) return null;
          return {
            timeLabel: formatEachDate(d),
            countLabel: "",
          };
        })
        .filter(Boolean);
    }

    const grouped = new Map();
    downloadsRows.forEach((row) => {
      const d = parseToDate(row?.downloaded_at);
      if (!d) return;
      const key = formatDateKey(d, downloadsViewMode);
      grouped.set(key, (grouped.get(key) || 0) + 1);
    });

    return Array.from(grouped.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, count]) => ({
        timeLabel: key,
        countLabel: `${count}次`,
      }));
  }, [downloadsRows, downloadsViewMode]);

  const handleOpenDownloads = async (item) => {
    setDownloadsModalOpen(true);
    setDownloadsLoading(true);
    setDownloadsRows([]);
    setDownloadsViewMode("each");
    setDownloadsTargetTitle(item.title || "");

    try {
      const data = await fetchAiLearningResourceDownloads(item.id);
      setDownloadsRows(data?.rows ?? []);
    } catch {
      showToast("下載紀錄載入失敗", true);
      setDownloadsRows([]);
    } finally {
      setDownloadsLoading(false);
    }
  };

  return (
    <RightPanelLayout title='AI 智庫'>
      <div className={styles.listToolbar}>
        <div className={styles.listToolbarTitle}>教學資源列表</div>
        <div className={styles.toolbarActions}>
          <div className={styles.searchBar}>
            <button
              type='button'
              className={styles.searchBarIconBtn}
              onClick={applyListSearch}
              aria-label='搜尋'
            >
              <HiOutlineSearch className={styles.searchBarIcon} />
            </button>
            <input
              type='search'
              className={styles.searchBarInput}
              value={searchInput}
              placeholder='搜尋資源標題'
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  applyListSearch();
                }
              }}
              aria-label='搜尋資源標題'
            />
            {searchInput ? (
              <button
                type='button'
                className={styles.searchBarClear}
                onClick={() => {
                  setSearchInput("");
                  setSearchApplied("");
                  setPage(1);
                }}
                aria-label='清除搜尋'
              >
                <MdClear className={styles.searchBarClearIcon} />
              </button>
            ) : null}
          </div>
          <Button type='primary' title='搜尋' onClick={applyListSearch} />
          <Button
            type='primary'
            title='新增'
            icon={<IoAddOutline fontSize={20} />}
            onClick={() => navigate("/dashboard/tdp/ai-learning-resources/new")}
          />
        </div>
      </div>

      {!listLoading && total > 0 ? (
        <div className={styles.tablePaginationTop}>
          <PaginationNew
            onPageChange={onPageChange}
            pageCount={page}
            limit={LIMIT}
            totalPages={totalPages}
            totalData={total}
          />
        </div>
      ) : null}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>標題</th>
              <th>上架</th>
              <th>上傳時間</th>
              <th className={styles.centerCell}>總下載次數</th>
              <th className={styles.centerCell}>下載時間</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {listLoading ? (
              <tr>
                <td colSpan={6} className={styles.tableLoadingCell}>
                  載入中…
                </td>
              </tr>
            ) : null}
            {!listLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={6} className={styles.tableEmptyCell}>
                  尚無資料
                </td>
              </tr>
            ) : null}
            {!listLoading
              ? rows.map((item) => (
                  <tr key={item.id}>
                    <td className={styles.tableTitle}>
                      {mutedUnset(item.title || EMPTY_DISPLAY)}
                    </td>
                    <td>
                      <div className={styles.publishCell}>
                        <Switcher
                          isChecked={Boolean(item.isPublished)}
                          onChange={() =>
                            handleTogglePublished(item.id, item.isPublished)
                          }
                        />
                      </div>
                    </td>
                    <td>{mutedUnset(formatUploadedDateOnly(item.uploadedAt))}</td>
                    <td className={styles.centerCell}>{Number(item.downloadCount) || 0}</td>
                    <td className={styles.centerCell}>
                      <button
                        type='button'
                        className={styles.downloadPopupButton}
                        onClick={() => handleOpenDownloads(item)}
                        aria-label='查看下載時間'
                      >
                        <CgList size={16} />
                      </button>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          type='button'
                          className={styles.editButton}
                          onClick={() =>
                            navigate(
                              `/dashboard/tdp/ai-learning-resources/${item.id}/edit`,
                            )
                          }
                          aria-label='編輯教學資源'
                        >
                          <MdDriveFileRenameOutline />
                        </button>
                        <button
                          type='button'
                          className={styles.deleteButton}
                          onClick={() => setDeleteTargetId(item.id)}
                          aria-label='刪除教學資源'
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              : null}
          </tbody>
        </table>
      </div>

      <Alert
        isOpen={deleteTargetId != null}
        onClose={() => !deleteSubmitting && setDeleteTargetId(null)}
        onConfirm={handleDelete}
        leftButtonText='取消'
        rightButtonText={deleteSubmitting ? "刪除中…" : "刪除"}
        mainTitle='確認刪除教學資源卡片？'
        subTitle='刪除後無法復原。'
        isWarning
      />

      <Modal
        isOpen={downloadsModalOpen}
        size='sm'
        width='min(460px, calc(100vw - 32px))'
        height='min(640px, calc(100vh - 48px))'
      >
        <div className={styles.downloadModalRoot}>
          <div className={styles.downloadModalHeader}>
            <div className={styles.downloadModalTitleWrap}>
              <h3 className={styles.downloadModalTitle}>下載時間</h3>
              {downloadsTargetTitle ? (
                <p className={styles.downloadModalSubTitle}>{downloadsTargetTitle}</p>
              ) : null}
            </div>
            <button
              type='button'
              className={styles.downloadModalClose}
              onClick={() => setDownloadsModalOpen(false)}
              aria-label='關閉下載時間彈窗'
            >
              <MdClear size={18} />
            </button>
          </div>

          <div className={styles.downloadModeTabs}>
            {DOWNLOAD_VIEW_MODES.map((mode) => (
              <button
                key={mode.key}
                type='button'
                className={`${styles.downloadModeTab} ${
                  downloadsViewMode === mode.key ? styles.downloadModeTabActive : ""
                }`}
                onClick={() => setDownloadsViewMode(mode.key)}
              >
                {mode.label}
              </button>
            ))}
          </div>

          <div className={styles.downloadListWrap}>
            {downloadsLoading ? (
              <div className={styles.downloadListEmpty}>載入中…</div>
            ) : aggregatedDownloadRows.length === 0 ? (
              <div className={styles.downloadListEmpty}>尚無下載紀錄</div>
            ) : (
              <ul className={styles.downloadList}>
                {aggregatedDownloadRows.map((line, idx) => (
                  <li
                    key={`${line.timeLabel}-${line.countLabel}-${idx}`}
                    className={styles.downloadListItem}
                  >
                    <div className={styles.downloadListItemRow}>
                      <span className={styles.downloadTimeText}>{line.timeLabel}</span>
                      {line.countLabel ? (
                        <span className={styles.downloadCountText}>
                          {line.countLabel}
                        </span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Modal>

      <Toast
        show={toastState.show}
        title={toastState.title}
        subtitle={toastState.subtitle}
        error={toastState.error}
      />
    </RightPanelLayout>
  );
};

export default AiLearningResourcesList;
